/**
 * Shared links and the preview ladder.
 *
 * While `person-panel` is unreleased on a stage, the panel is reachable only
 * through `?preview=2` (or a higher rung). The preview mode persists in
 * `sessionStorage` (`PreviewProvider`), which is per TAB: a link pasted into a
 * new tab, or sent to a colleague, arrives with no preview and lands on
 * nothing (the standalone route redirects to /dashboard; the in-list
 * `?person=` is ignored and the list opens without the panel).
 *
 * So while the panel is unreleased, Copy link appends `preview=<the panel's
 * phase>` — the LOWEST rung that reveals it (2), not the copier's own mode:
 * someone in `?preview=all` should not hand out the whole radar view with a
 * person link. Once `person-panel` is released on the stage, no param is
 * added and the links are plain.
 *
 * AS OF 2026-09-23 the panel is released on BOTH registries (the production
 * freeze lifted), so in practice every link this module hands out today is
 * plain, on every stage. The rung logic is kept rather than deleted because it
 * is the general rule for a phase-gated share link — the next such feature
 * inherits it — and because a re-freeze would otherwise silently start handing
 * out links that land on nothing. Its unreleased branch is pinned in
 * `sharePreview.test.ts` with a synthetic registry.
 */
import { type FeatureFlags, activeFlags, isPageReleased } from "../../config/featureFlags";
import { pageOwnPhase } from "../useFeatureAccess";

/** The preview rung a shared person link must carry on this stage, or null
 *  when the panel is released and needs none. */
export function personLinkPreviewPhase(flags: FeatureFlags = activeFlags()): number | null {
  if (isPageReleased(flags, "linkedin", "person-panel")) return null;
  return pageOwnPhase("person-panel");
}

/** `pathAndQuery` with `preview=<phase>` set (replacing any `preview` it
 *  already carried), or unchanged when `phase` is null. */
export function withPreviewParam(pathAndQuery: string, phase: number | null): string {
  if (phase == null) return pathAndQuery;
  const url = new URL(pathAndQuery, "http://share.invalid");
  url.searchParams.set("preview", String(phase));
  return `${url.pathname}${url.search}${url.hash}`;
}

/** The absolute URL a Copy link button hands out for a person view. */
export function personShareUrl(pathAndQuery: string, flags: FeatureFlags = activeFlags()): string {
  return `${window.location.origin}${withPreviewParam(pathAndQuery, personLinkPreviewPhase(flags))}`;
}
